import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { Popover } from "antd";
import type { Dayjs } from "dayjs";
import {
  HiOutlineEye,
  HiOutlineCheckCircle,
  HiOutlinePaperAirplane,
  HiOutlineXCircle,
  HiOutlineInboxStack,
  HiOutlineArrowLeft,
  HiOutlineArrowTopRightOnSquare,
  HiOutlineTrash,
  HiOutlineXMark,
  HiOutlineClock,
  HiOutlineCalendar,
  HiOutlineAcademicCap,
  HiOutlineUserGroup,
  HiOutlineChevronLeft,
  HiOutlineChevronRight,
} from "react-icons/hi2";
import { Panel, FormButton, DateRangePicker } from "../../ui";
import { autoBiddingApi } from "../../api";
import { IAutoBidJob, IAutoBidStats, ITeamMemberRecord } from "../../data-access";
import { DataTablePagination } from "../../ui/data-table/data-table-pagination/data-table-pagination";
import { RootState } from "../../store";
import { capitalize } from "../../util";
import { AutoBiddingGetStarted } from "./auto-bidding-get-started";
import "../../ui/simple-table";
import "./auto-bidding.scss";

const PERIOD_FILTERS = [
  { value: "current_week", label: "Current Week" },
  { value: "last_week", label: "Last Week" },
  { value: "current_month", label: "Current Month" },
  { value: "last_month", label: "Last Month" },
];

const STATUS_CONFIG: Record<string, { label: string; color: string }> = {
  scanned: { label: "Scanned", color: "#78778B" },
  matched: { label: "Matched", color: "#22c55e" },
  rejected: { label: "Rejected", color: "#F87171" },
  submitted: { label: "Submitted", color: "#60A5FA" },
  failed: { label: "Failed", color: "#fb923c" },
  skipped: { label: "Skipped", color: "#FBBF24" },
};

const stripHtml = (s: string) => s.replace(/<[^>]*>/g, "");
const formatDate = (d: string) => {
  const date = new Date(d);
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
};

const timeAgo = (d: string) => {
  const now = Date.now();
  const then = new Date(d).getTime();
  if (isNaN(then)) return d;
  const diff = Math.floor((now - then) / 1000);
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} hr ago`;
  if (diff < 172800) return "yesterday";
  return `${Math.floor(diff / 86400)} days ago`;
};
const getScoreColor = (score: number | null) => {
  if (score === null) return "";
  if (score >= 80) return "ab-score--green";
  if (score >= 60) return "ab-score--yellow";
  return "ab-score--red";
};

// ==========================================
// FULLSCREEN JOB DETAIL (Upwork-style layout)
// ==========================================

const JobDetailOverlay = ({ job, onClose, onPrev, onNext, hasPrev, hasNext }: { job: IAutoBidJob; onClose: () => void; onPrev?: () => void; onNext?: () => void; hasPrev?: boolean; hasNext?: boolean }) => {
  const mr = job.data?.match_result;
  const j = job.data?.job;
  const title = stripHtml(job.data?.title || j?.title || job.u_job_id);
  const upworkUrl = `https://www.upwork.com/jobs/${job.u_job_id}`;

  // Lock the underlying page scroll while the fullscreen detail is open
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);

  return (
    <div className="abd-fullscreen">
      {/* Header */}
      <div className="abd-fullscreen__header">
        <button className="abd-fullscreen__back" onClick={onClose}><HiOutlineArrowLeft size={18} /></button>
        <h2 className="abd-fullscreen__title">{title}</h2>
        <span className="abd-fullscreen__sep" />
        <span className="ab-status-badge ab-status-badge--lg" style={{ background: `${STATUS_CONFIG[job.status]?.color}20`, color: STATUS_CONFIG[job.status]?.color }}>
          {STATUS_CONFIG[job.status]?.label}
        </span>
        {job.match_score !== null && <span className={`abd-fullscreen__score ${getScoreColor(job.match_score)}`}>{job.match_score}/100</span>}
        <span className="abd-fullscreen__spacer" />
        <div className="abd-fullscreen__nav">
          <button className="abd-fullscreen__nav-btn" disabled={!hasPrev} onClick={onPrev} title="Previous job"><HiOutlineChevronLeft size={16} /></button>
          <button className="abd-fullscreen__nav-btn" disabled={!hasNext} onClick={onNext} title="Next job"><HiOutlineChevronRight size={16} /></button>
        </div>
        <FormButton theme="grey" className="abd-fullscreen__upwork-btn" onClick={() => window.open(upworkUrl, '_blank')}>
          View on Upwork <HiOutlineArrowTopRightOnSquare size={13} />
        </FormButton>
        <button className="abd-fullscreen__close" onClick={onClose}><HiOutlineXMark size={18} /></button>
      </div>

      {/* Body: sidebar (timeline + client) + main (job + AI) */}
      <div className="abd-fullscreen__body">
        {/* Sidebar: Timeline + About the Client */}
        <div className="abd-fullscreen__sidebar">
          <div className="abd-fullscreen__sidebar-scroll">
            {/* Timeline */}
            <h3 className="abd-sidebar-title">Status Timeline</h3>
            <div className="abd-timeline">
              <div className="abd-timeline-item">
                <span className="abd-timeline-dot" style={{ background: "#78778B" }} />
                <div className="abd-timeline-info">
                  <span className="abd-timeline-label">Scanned</span>
                  <span className="abd-timeline-date">{formatDate(job.created_at)}</span>
                </div>
              </div>
              {job.status !== "scanned" && (
                <div className="abd-timeline-item">
                  <span className="abd-timeline-dot" style={{ background: job.status === "rejected" ? "#F87171" : "#22c55e" }} />
                  <div className="abd-timeline-info">
                    <span className="abd-timeline-label">{job.status === "rejected" ? "Rejected by AI" : "Matched by AI"}{job.match_score !== null && ` (${job.match_score}/100)`}</span>
                  </div>
                </div>
              )}
              {job.status === "submitted" && (
                <div className="abd-timeline-item">
                  <span className="abd-timeline-dot" style={{ background: "#60A5FA" }} />
                  <div className="abd-timeline-info"><span className="abd-timeline-label">Proposal Submitted</span></div>
                </div>
              )}
              {job.status === "failed" && (
                <div className="abd-timeline-item">
                  <span className="abd-timeline-dot" style={{ background: "#fb923c" }} />
                  <div className="abd-timeline-info"><span className="abd-timeline-label">Submission Failed</span></div>
                </div>
              )}
              {job.status === "skipped" && (
                <div className="abd-timeline-item">
                  <span className="abd-timeline-dot" style={{ background: "#FBBF24" }} />
                  <div className="abd-timeline-info"><span className="abd-timeline-label">Skipped</span></div>
                </div>
              )}
            </div>

            {/* Rejection reason */}
            {job.reject_reason && (
              <div className="abd-sidebar-reason">
                <span className="abd-sidebar-reason__label">Reason:</span>
                {job.reject_reason}
              </div>
            )}
          </div>
        </div>

        {/* Main content */}
        <div className="abd-fullscreen__main">
          {/* Job Description + Client (2-column like Upwork) */}
          {j && (
            <div className="abd-card">
              <h3 className="abd-card__title">Job Details</h3>
              <div className="abd-job-layout">
                {/* Left: Job content */}
                <div className="abd-job-layout__left">
              {j.description && <p className="abd-description">{j.description}</p>}

              {/* Three info cards (Upwork style: workload, duration, level) */}
              {(j.jobWorkload || j.engagementDuration || j.experienceLevel) && (
                <div className="abd-info-cards">
                  {j.jobWorkload && (
                    <div className="abd-info-card">
                      <HiOutlineClock size={18} />
                      <div><strong>{j.jobWorkload}</strong><span>{j.paymentType || 'Hourly'}</span></div>
                    </div>
                  )}
                  {j.engagementDuration && (
                    <div className="abd-info-card">
                      <HiOutlineCalendar size={18} />
                      <div><strong>{j.engagementDuration}</strong><span>Duration</span></div>
                    </div>
                  )}
                  {j.experienceLevel && (
                    <div className="abd-info-card">
                      <HiOutlineAcademicCap size={18} />
                      <div><strong>{j.experienceLevel}</strong><span>Experience Level</span></div>
                    </div>
                  )}
                </div>
              )}

              {/* Project details row */}
              <div className="abd-project-row">
                {j.jobPosted && <span>Posted: <strong>{timeAgo(j.jobPosted)}</strong></span>}
                {j.budget && <span>Budget: <strong>${j.budget}</strong></span>}
                {(j.extendedBudgetMin || j.extendedBudgetMax) && <span>Hourly: <strong>${j.extendedBudgetMin}-${j.extendedBudgetMax}</strong></span>}
                {j.projectType && <span>Project Type: <strong>{j.projectType}</strong></span>}
                {j.jobCategory && <span>Category: <strong>{j.jobCategory}</strong></span>}
                {j.isContractToHire && <span>Contract to Hire: <strong className="abd-text--green">Yes</strong></span>}
                {j.isPremium && <span>Premium: <strong className="abd-text--green">Yes</strong></span>}
                {j.isAiGenerated && <span>AI Generated: <strong>Yes</strong></span>}
                {j.isFirstJobPost && <span>First Job Post: <strong>Yes</strong></span>}
                {j.numberOfPositionsToHire && j.numberOfPositionsToHire > 1 && <span>Positions: <strong>{j.numberOfPositionsToHire}</strong></span>}
              </div>

              {/* Skills */}
              {((j.requiredSkills && j.requiredSkills.length > 0) || (j.additionalSkills && j.additionalSkills.length > 0)) && (
                <div className="abd-section-block">
                  <h4>Skills and Expertise</h4>
                  {j.requiredSkills && j.requiredSkills.length > 0 && (
                    <>
                      <span className="abd-section-sublabel">Required</span>
                      <div className="abd-skills">{j.requiredSkills.map((s, i) => <span key={i} className="abd-skill abd-skill--matched">{s}</span>)}</div>
                    </>
                  )}
                  {j.additionalSkills && j.additionalSkills.length > 0 && (
                    <>
                      <span className="abd-section-sublabel" style={{ marginTop: 8 }}>Additional</span>
                      <div className="abd-skills">{j.additionalSkills.map((s, i) => <span key={i} className={`abd-skill ${s.relevance === 'MANDATORY' ? 'abd-skill--matched' : 'abd-skill--optional'}`}>{s.name}</span>)}</div>
                    </>
                  )}
                </div>
              )}

              {/* Activity on this job */}
              {(j.proposals !== undefined || j.jobTotalApplicants || j.applicantsBidsStats?.avg) && (
                <div className="abd-section-block">
                  <h4>Activity on this Job</h4>
                  <div className="abd-activity-list">
                    {j.proposals !== undefined && <div>Proposals: <strong>{j.proposals}</strong></div>}
                    {j.jobTotalApplicants !== undefined && j.jobTotalApplicants > 0 && <div>Total Applicants: <strong>{j.jobTotalApplicants}</strong></div>}
                    {j.jobLastBuyerActivity && <div>Last viewed by client: <strong>{timeAgo(j.jobLastBuyerActivity)}</strong></div>}
                    {j.interviews !== undefined && <div>Interviewing: <strong>{j.interviews}</strong></div>}
                    {j.invites !== undefined && j.invites > 0 && <div>Invites sent: <strong>{j.invites}</strong></div>}
                    {j.jobInvitationsSent !== undefined && j.jobInvitationsSent > 0 && <div>Invitations sent: <strong>{j.jobInvitationsSent}</strong></div>}
                    {j.jobTotalHired !== undefined && j.jobTotalHired > 0 && <div>Hired: <strong>{j.jobTotalHired}</strong></div>}
                  </div>
                  {j.applicantsBidsStats && (j.applicantsBidsStats.avg || j.applicantsBidsStats.min || j.applicantsBidsStats.max) && (
                    <>
                    <h4 style={{ marginTop: 12 }}>Bid Range</h4>
                    <div className="abd-info-cards">
                      {j.applicantsBidsStats.max !== undefined && (
                        <div className="abd-info-card">
                          <div><strong>${Math.round(j.applicantsBidsStats.max)}</strong><span>High Bid</span></div>
                        </div>
                      )}
                      {j.applicantsBidsStats.avg !== undefined && (
                        <div className="abd-info-card">
                          <div><strong>${Math.round(j.applicantsBidsStats.avg)}</strong><span>Avg Bid</span></div>
                        </div>
                      )}
                      {j.applicantsBidsStats.min !== undefined && (
                        <div className="abd-info-card">
                          <div><strong>${Math.round(j.applicantsBidsStats.min)}</strong><span>Low Bid</span></div>
                        </div>
                      )}
                    </div>
                    </>
                  )}
                </div>
              )}

              {/* Qualifications */}
              {j.qualificationsMatches && j.qualificationsMatches.filter(q => q.label).length > 0 && (
                <div className="abd-section-block">
                  <h4>Qualifications</h4>
                  <div className="abd-quals">
                    {j.qualificationsMatches.filter(q => q.label).map((q, i) => (
                      <div key={i} className={`abd-qual ${q.met ? 'abd-qual--met' : 'abd-qual--unmet'}`}>
                        <span className="abd-qual-dot" />
                        <span className="abd-qual-label">{q.label}</span>
                        <span className="abd-qual-value">{q.freelancerValue}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Requirements */}
              {(j.requiredLanguages?.length || j.preferredRegions?.length || (j.minJobSuccessScore && j.minJobSuccessScore > 0)) && (
                <div className="abd-section-block">
                  <h4>Requirements</h4>
                  <div className="abd-activity-list">
                    {j.requiredLanguages && j.requiredLanguages.length > 0 && <div>Languages: <strong>{j.requiredLanguages.join(', ')}</strong></div>}
                    {j.preferredRegions && j.preferredRegions.length > 0 && <div>Preferred Regions: <strong>{j.preferredRegions.join(', ')}</strong></div>}
                    {j.minJobSuccessScore !== undefined && j.minJobSuccessScore > 0 && <div>Min Job Success Score: <strong>{j.minJobSuccessScore}%</strong></div>}
                  </div>
                </div>
              )}

              {/* Screening Questions */}
              {j.screeningQuestions && j.screeningQuestions.length > 0 && (
                <div className="abd-section-block">
                  <h4>Screening Questions ({j.screeningQuestions.length})</h4>
                  <ol className="abd-questions abd-questions--numbered">
                    {j.screeningQuestions.map((q, i) => <li key={i}>{q}</li>)}
                  </ol>
                </div>
              )}
                </div>{/* end left */}

                {/* Right: About the Client */}
                <div className="abd-job-layout__right">
                  <h4 className="abd-client-title">About the Client</h4>
                  <div className="abd-client">
                    <div className={`abd-client__row ${j.paymentVerification ? 'abd-client__row--green' : 'abd-client__row--red'}`}>
                      <span className="abd-client__dot" />
                      {j.paymentVerification ? 'Payment method verified' : 'Payment not verified'}
                    </div>

                    {j.rating && (
                      <div className="abd-client__block">
                        <strong>{j.rating} rating</strong>
                        <span>{j.totalReviews || 0} review{(j.totalReviews || 0) !== 1 ? 's' : ''}</span>
                      </div>
                    )}

                    {j.clientCountry && (
                      <div className="abd-client__block">
                        <strong>{j.clientCountry}</strong>
                        {j.clientCity && <span>{j.clientCity}</span>}
                      </div>
                    )}

                    {j.clientJobsPosted !== undefined && (
                      <div className="abd-client__block">
                        <strong>{j.clientJobsPosted} jobs posted</strong>
                        <span>{j.hireRate || '0%'} hire rate, {j.clientJobsOpen || 0} open jobs</span>
                      </div>
                    )}

                    {j.totalSpent && (
                      <div className="abd-client__block">
                        <strong>${j.totalSpent} total spent</strong>
                        {j.clientActiveAssignments !== undefined && <span>{j.hired || 0} hires, {j.clientActiveAssignments} active</span>}
                      </div>
                    )}

                    {j.clientAvgHourlyRate !== undefined && j.clientAvgHourlyRate > 0 && (
                      <div className="abd-client__block">
                        <strong>${Math.round(j.clientAvgHourlyRate * 100) / 100}/hr avg rate paid</strong>
                      </div>
                    )}

                    {j.memberSince && (
                      <div className="abd-client__block abd-client__block--muted">
                        Member since {new Date(j.memberSince).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                      </div>
                    )}

                    {j.clientCompanyName && (
                      <div className="abd-client__block">
                        <strong>{j.clientCompanyName}</strong>
                        {j.clientCompanyIndustry && <span>{j.clientCompanyIndustry}</span>}
                      </div>
                    )}

                    {j.requiredConnects !== undefined && (
                      <div className="abd-client__block" style={{ marginTop: 8, paddingTop: 8, borderTop: `1px solid ${String.fromCharCode(35)}2C2C30` }}>
                        <span>Connects: <strong>{j.requiredConnects}</strong></span>
                        {j.freelancerHourlyRate !== undefined && j.freelancerHourlyRate > 0 && <span>Your rate: <strong>${j.freelancerHourlyRate}/hr</strong></span>}
                      </div>
                    )}
                  </div>
                </div>{/* end right */}
              </div>{/* end job-layout */}
            </div>
          )}

          {/* Client Reviews & History */}
          {j && ((j.reviewsFeedback && j.reviewsFeedback.length > 0) || (j.enhancedWorkHistory && j.enhancedWorkHistory.length > 0) || (j.openJobsTitles && j.openJobsTitles.length > 0)) && (
            <div className="abd-card">
              <h3 className="abd-card__title">Client History</h3>

              {j.enhancedWorkHistory && j.enhancedWorkHistory.length > 0 && (
                <div className="abd-section-block">
                  <h4>Recent Contracts</h4>
                  <div className="abd-work-history">
                    {j.enhancedWorkHistory.map((w, i) => (
                      <div key={i} className="abd-wh-item">
                        <div className="abd-wh-item__header">
                          <span className="abd-wh-item__title">{w.title}</span>
                          <span className="abd-wh-item__meta">
                            {w.totalCharge !== undefined && w.totalCharge > 0 ? `$${w.totalCharge}` : ''}
                          </span>
                        </div>
                        {/* Client wrote about freelancer */}
                        <div className="abd-wh-item__review">
                          <span className="abd-wh-item__prefix">Client to freelancer:</span>
                          <span className="abd-wh-item__stars">{w.score}/5</span>
                          {w.comment && <p className="abd-wh-item__text">{w.comment}</p>}
                        </div>
                        {/* Freelancer wrote about client */}
                        {((w.clientScore !== undefined && w.clientScore > 0) || w.clientComment) && (
                          <div className="abd-wh-item__review abd-wh-item__review--second">
                            <span className="abd-wh-item__prefix">Freelancer to client:</span>
                            <span className="abd-wh-item__stars">{w.clientScore || 0}/5</span>
                            {w.clientComment && <p className="abd-wh-item__text">{w.clientComment}</p>}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {j.reviewsFeedback && j.reviewsFeedback.length > 0 && (
                <div className="abd-section-block">
                  <h4>Client Reviews</h4>
                  <div className="abd-reviews">
                    {j.reviewsFeedback.map((r, i) => (
                      <div key={i} className="abd-review">
                        {j.reviewsTitles?.[i] && <span className="abd-review__title">{j.reviewsTitles[i]}</span>}
                        <p className="abd-review__text">{r}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {j.openJobsTitles && j.openJobsTitles.length > 0 && (
                <div className="abd-section-block">
                  <h4>Other Open Jobs ({j.openJobsTitles.length})</h4>
                  <ul className="abd-open-jobs">
                    {j.openJobsTitles.map((t, i) => <li key={i}>{t}</li>)}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* AI Analysis */}
          {mr ? (
            <div className="abd-card">
              <div className="abd-card__title-row">
                <h3 className="abd-card__title">AI Analysis</h3>
                <div className="abd-verdict">
                  <span className={`abd-verdict-badge abd-verdict-badge--${mr.verdict?.toLowerCase()}`}>{mr.verdict}</span>
                  {mr.score !== undefined && <span className="abd-verdict-score">{mr.score}/100</span>}
                </div>
              </div>
              {mr.summary && <p className="abd-summary">{mr.summary}</p>}

              {mr.skill_match && (
                <div className="abd-section-block">
                  <h4>Skills Match ({mr.skill_match.overlap_percent}%)</h4>
                  <div className="abd-skills">
                    {mr.skill_match.matched?.map((s, i) => <span key={i} className="abd-skill abd-skill--matched">{s}</span>)}
                    {mr.skill_match.missing?.map((s, i) => <span key={i} className="abd-skill abd-skill--missing">{s}</span>)}
                  </div>
                </div>
              )}

              {mr.red_flags_check && mr.red_flags_check.triggered?.length > 0 && (
                <div className="abd-section-block">
                  <h4>Red Flags</h4>
                  <div className="abd-flags">
                    {mr.red_flags_check.triggered.map((f, i) => (
                      <div key={i} className="abd-flag-item">{f}</div>
                    ))}
                  </div>
                </div>
              )}

              {mr.match_criteria && mr.match_criteria.length > 0 && (
                <div className="abd-section-block">
                  <h4>Match Criteria</h4>
                  <div className="abd-criteria">
                    {mr.match_criteria.map((c, i) => (
                      <div key={i} className={`abd-criterion-card ${c.passed ? "abd-criterion-card--pass" : "abd-criterion-card--fail"}`}>
                        <div className="abd-criterion-card__header">
                          <span className="abd-criterion-card__dot" />
                          <span className="abd-criterion-card__rule">{c.rule}</span>
                          <span className={`abd-criterion-card__badge ${c.passed ? "abd-criterion-card__badge--pass" : "abd-criterion-card__badge--fail"}`}>
                            {c.passed ? "Pass" : "Fail"}
                          </span>
                        </div>
                        {c.note && <p className="abd-criterion-card__note">{c.note}</p>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {mr.client_assessment && (
                <div className="abd-section-block">
                  <h4>Client Assessment</h4>
                  <p className="abd-ai-text">Risk: <strong>{mr.client_assessment.risk_level}</strong></p>
                  <p className="abd-ai-text">{mr.client_assessment.notes}</p>
                </div>
              )}
            </div>
          ) : job.reject_reason ? (
            <div className="abd-card">
              <h3 className="abd-card__title">Rejection</h3>
              <div className="abd-sidebar-reason"><span className="abd-sidebar-reason__label">Reason:</span>{job.reject_reason}</div>
            </div>
          ) : (
            <div className="abd-card">
              <h3 className="abd-card__title">AI Analysis</h3>
              <p className="abd-empty-text">No AI analysis data available for this job.</p>
            </div>
          )}

          {/* Submission Details */}
          {job.data?.submission && (
            <div className="abd-card">
              <h3 className="abd-card__title">Proposal Submitted</h3>

              {job.data.submission.coverLetter && (
                <div className="abd-section-block">
                  <h4>Cover Letter</h4>
                  <div className="abd-cover-letter">{job.data.submission.coverLetter}</div>
                </div>
              )}

              {job.data.submission.answers && job.data.submission.answers.length > 0 && (
                <div className="abd-section-block">
                  <h4>Screening Answers ({job.data.submission.answers.length})</h4>
                  <div className="abd-answers">
                    {job.data.submission.answers.map((a, i) => (
                      <div key={i} className="abd-answer">
                        <div className="abd-answer__question">{i + 1}. {a.question}</div>
                        <div className="abd-answer__text">{a.answer}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {job.data.submission.rate !== undefined && job.data.submission.rate > 0 && (
                <div className="abd-section-block">
                  <h4>Rate</h4>
                  <p className="abd-ai-text">${job.data.submission.rate}/hr</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ==========================================
// MAIN LIST VIEW
// ==========================================

export const AutoBidding = (): JSX.Element => {
  const teamMembers = useSelector((state: RootState) => state.team.members);
  const [stats, setStats] = useState<IAutoBidStats | null>(null);
  const [jobs, setJobs] = useState<IAutoBidJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(15);
  const [selectedJob, setSelectedJob] = useState<IAutoBidJob | null>(null);
  const [timePeriod, setTimePeriod] = useState("current_week");
  const [rangeDate, setRangeDate] = useState<[Dayjs | null, Dayjs | null]>([null, null]);
  const [userFilter, setUserFilter] = useState("");
  const [userPickerOpen, setUserPickerOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState("");
  // Has Auto-Bidding ever produced activity? null = checking. Drives the
  // get-started guide, which hides as soon as any job has been scanned.
  const [hasActivity, setHasActivity] = useState<boolean | null>(null);

  useEffect(() => {
    autoBiddingApi
      .getJobs(1, 1, {})
      .then((res: any) => setHasActivity((res.total || 0) > 0))
      .catch(() => setHasActivity(true)); // on error, don't block the page
  }, []);

  const filters = {
    time_period: timePeriod || undefined,
    date_from: rangeDate[0]?.format("YYYY-MM-DD") || undefined,
    date_to: rangeDate[1]?.format("YYYY-MM-DD") || undefined,
    user_id: userFilter || undefined,
    status: statusFilter || undefined,
  };

  const fetchData = () => {
    setStatsLoading(true);
    setLoading(true);
    autoBiddingApi.getStats(filters).then((res) => setStats(res)).catch(() => {}).finally(() => setStatsLoading(false));
    autoBiddingApi.getJobs(page, perPage, filters).then((res: any) => { setJobs(res.data || []); setTotal(res.total || 0); if ((res.total || 0) > 0) setHasActivity(true); }).catch(() => {}).finally(() => setLoading(false));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchData(); }, [page, perPage, timePeriod, rangeDate, userFilter, statusFilter]);

  const handlePagination = (newPerPage: number, newPage: number) => { setPerPage(newPerPage); setPage(newPage); };
  const handleClear = async () => { if (!window.confirm("Clear all auto-bidding data?")) return; await autoBiddingApi.clearAll(); fetchData(); };

  const getInitials = (m: ITeamMemberRecord) => (m.full_name || m.email).split(" ").map((w) => w[0]).join("").toUpperCase().slice(0, 2);
  const selectedUser = teamMembers.find((m) => String(m.id) === userFilter);

  const userPicker = (
    <Popover
      content={
        <div className="tpl-user-picker__list">
          <div className={`tpl-user-picker__item ${!userFilter ? "active" : ""}`} onClick={() => { setUserFilter(""); setUserPickerOpen(false); }}>
            <span className="tpl-user-picker__avatar tpl-user-picker__avatar--all"><HiOutlineUserGroup size={16} /></span>
            <span>All Members</span>
          </div>
          {teamMembers.map((member) => (
            <div key={member.id} className={`tpl-user-picker__item ${userFilter === String(member.id) ? "active" : ""}`} onClick={() => { setUserFilter(String(member.id)); setUserPickerOpen(false); }}>
              <span className="tpl-user-picker__avatar">{getInitials(member)}</span>
              <span>{member.full_name ?? capitalize(member.email.substring(0, member.email.indexOf("@")))}</span>
            </div>
          ))}
        </div>
      }
      trigger="click"
      open={userPickerOpen}
      onOpenChange={setUserPickerOpen}
      placement="bottomRight"
      overlayClassName="tpl-user-picker__popover"
    >
      <div className="tpl-user-picker__trigger">
        {selectedUser ? (
          <span className="tpl-user-picker__avatar">{getInitials(selectedUser)}</span>
        ) : (
          <span className="tpl-user-picker__avatar tpl-user-picker__avatar--all"><HiOutlineUserGroup size={16} /></span>
        )}
      </div>
    </Popover>
  );

  const openDetail = (job: IAutoBidJob) => {
    if (job.data?.match_result || job.data?.job) { setSelectedJob(job); return; }
    autoBiddingApi.getJob(job.id).then((full) => setSelectedJob(full)).catch(() => setSelectedJob(job));
  };

  // Show the get-started guide only when we've confirmed there is no activity yet
  const showGuide = hasActivity === false;

  return (
    <>
      {selectedJob && (() => {
        const idx = jobs.findIndex(j => j.id === selectedJob.id);
        const navigate = (dir: number) => {
          const next = jobs[idx + dir];
          if (next) {
            if (next.data?.match_result || next.data?.job) { setSelectedJob(next); return; }
            autoBiddingApi.getJob(next.id).then((full) => setSelectedJob(full)).catch(() => setSelectedJob(next));
          }
        };
        return <JobDetailOverlay job={selectedJob} onClose={() => setSelectedJob(null)} onPrev={() => navigate(-1)} onNext={() => navigate(1)} hasPrev={idx > 0} hasNext={idx < jobs.length - 1} />;
      })()}
      <Panel className="auto-bidding" title="Auto-Bidding" nodeInTitleRow={showGuide ? undefined : (
        <div className="tpl-title-filters">
          {userPicker}
          <ul className="tpl-period-tabs">
            {PERIOD_FILTERS.map((p) => (
              <li key={p.value} className={timePeriod === p.value ? "active" : ""} onClick={() => { setTimePeriod(p.value); setRangeDate([null, null]); }}>
                {p.label}
              </li>
            ))}
          </ul>
          <DateRangePicker onChange={(dates) => {
            if (dates?.[0] && dates?.[1]) {
              setRangeDate(dates as [Dayjs, Dayjs]);
              setTimePeriod("");
            } else {
              setRangeDate([null, null]);
              setTimePeriod("current_week");
            }
          }} />
        </div>
      )}>
        {showGuide ? (
          <AutoBiddingGetStarted />
        ) : (
        <>
        <div className="ab-stats">
          {statsLoading || hasActivity === null ? [1,2,3,4].map((i) => (<div key={i} className="ab-stats__item"><div className="shimmer shimmer--line" style={{ width: 60, height: 36 }} /><div className="shimmer shimmer--line" style={{ width: 100, height: 12, marginTop: 6 }} /></div>)) : <>
            <div className="ab-stats__item"><div className="ab-stats__value">{stats?.total ?? 0}</div><div className="ab-stats__label"><HiOutlineEye size={14} /> Jobs Scanned</div></div>
            <div className="ab-stats__item"><div className="ab-stats__value">{stats?.matched ?? 0}</div><div className="ab-stats__label"><HiOutlineCheckCircle size={14} /> Matched</div></div>
            <div className="ab-stats__item"><div className="ab-stats__value">{stats?.submitted ?? 0}</div><div className="ab-stats__label"><HiOutlinePaperAirplane size={14} /> Proposals Sent</div></div>
            <div className="ab-stats__item"><div className="ab-stats__value">{stats?.replies ?? 0}</div><div className="ab-stats__label"><HiOutlineInboxStack size={14} /> Replies</div></div>
          </>}
        </div>
        <div className="ab-divider" />
        <div className="ab-activity-header">
          <h2 className="ab-section-title">Job Activity</h2>
          <ul className="ab-status-tabs">
            {[
              { value: "", label: "All" },
              { value: "submitted", label: "Submitted" },
              { value: "rejected,skipped,failed,matched,scanned", label: "Skipped" },
            ].map((tab) => (
              <li key={tab.value} className={statusFilter === tab.value ? "active" : ""} onClick={() => { setStatusFilter(tab.value); setPage(1); }}>{tab.label}</li>
            ))}
          </ul>
        </div>
        {loading || hasActivity === null ? (
          <div className="simple-table"><div className="simple-table__header ab-table__grid"><span className="simple-table__col">Job</span><span className="simple-table__col">Status</span><span className="simple-table__col">Score</span><span className="simple-table__col">Date</span></div>
            {[1,2,3,4,5].map((i) => (<div key={i} className="simple-table__row ab-table__grid"><span className="simple-table__col"><div className="shimmer shimmer--line" style={{ width: "80%", height: 14 }} /></span><span className="simple-table__col"><div className="shimmer shimmer--line" style={{ width: 60, height: 14 }} /></span><span className="simple-table__col"><div className="shimmer shimmer--line" style={{ width: 30, height: 14 }} /></span><span className="simple-table__col"><div className="shimmer shimmer--line" style={{ width: 80, height: 14 }} /></span></div>))}</div>
        ) : jobs.length === 0 ? (
          <div className="ab-empty"><HiOutlineInboxStack size={32} /><p>No auto-bidding activity yet</p><span>Jobs will appear here once Auto-Bidding starts scanning.</span></div>
        ) : (
          <div className="simple-table"><div className="simple-table__header ab-table__grid"><span className="simple-table__col">Job</span><span className="simple-table__col">Status</span><span className="simple-table__col">Score</span><span className="simple-table__col">Date</span></div>
            {jobs.map((job) => (
              <div key={job.id} className="simple-table__row ab-table__grid ab-table__row" onClick={() => openDetail(job)}>
                <span className="simple-table__col simple-table__col--primary ab-table__title" data-label="">{stripHtml(job.data?.title || job.u_job_id)}</span>
                <span className="simple-table__col" data-label="Status"><span className="ab-status-badge" style={{ background: `${STATUS_CONFIG[job.status]?.color}20`, color: STATUS_CONFIG[job.status]?.color }}>{STATUS_CONFIG[job.status]?.label || job.status}</span></span>
                <span className={`simple-table__col simple-table__col--primary ${getScoreColor(job.match_score)}`} data-label="Score">{job.match_score !== null ? job.match_score : "-"}</span>
                <span className="simple-table__col" data-label="Date">{formatDate(job.created_at)}</span>
              </div>
            ))}
            {total > perPage && <DataTablePagination total={total} perPage={perPage} perPageOptions={[15, 25, 50]} currentPage={page} onPagination={handlePagination} />}
          </div>
        )}
        </>
        )}
      </Panel>
    </>
  );
};
